<template>
  <NuxtLayout name="saas">
    <div class="saas-page-head">
      <div class="saas-rounded-shape saas-rounded-shape__default-left-1"></div>
      <div class="saas-rounded-shape saas-rounded-shape__default-left-2"></div>
      <div class="saas-rounded-shape saas-rounded-shape__default-left-3"></div>
      <div class="saas-rounded-shape saas-rounded-shape__default-right-1"></div>
      <div class="saas-rounded-shape saas-rounded-shape__default-right-2"></div>
      <h1 class="saas-page-head__title">
        Contact our <strong>sales team</strong>
      </h1>
      <p class="saas-page-head__description">
        Explore enterprise pricing, data migration support, and tailored
        solutions.
      </p>
      <div class="saas-contact__columns">
        <div class="saas-contact__info">
          <div class="saas-contact__possibilities">
            <div class="saas-contact__possibility">
              <div class="saas-contact__possibility-icon">
                <i class="iconoir-check-circle"></i>
              </div>
              <div class="saas-contact__possibility-title">
                Explore use cases
              </div>
              <div class="saas-contact__possibility-description">
                Discover all the ways Baserow can power your business.
              </div>
            </div>
            <div class="saas-contact__possibility">
              <div class="saas-contact__possibility-icon">
                <i class="iconoir-check-circle"></i>
              </div>
              <div class="saas-contact__possibility-title">
                See Baserow in action
              </div>
              <div class="saas-contact__possibility-description">
                Request a demo for your team.
              </div>
            </div>
            <div class="saas-contact__possibility">
              <div class="saas-contact__possibility-icon">
                <i class="iconoir-check-circle"></i>
              </div>
              <div class="saas-contact__possibility-title">
                Explore flexible pricing options
              </div>
              <div class="saas-contact__possibility-description">
                See how Baserow could be your most cost-effective platform yet.
              </div>
            </div>
          </div>
          <div class="saas-contact__connected-wrapper">
            <div class="saas-contact__connected">
              <div class="saas-contact__text">
                Technical issues or product questions?
              </div>
              <div class="saas-contact__actions">
                <Button :to="{ name: 'contact' }" size="large" type="secondary"
                  >Contact support</Button
                >
              </div>
            </div>
          </div>
        </div>
        <div ref="form" class="saas-contact__form">
          <Alert v-if="finished" type="success" icon="iconoir-mail">
            <template #title>Message sent</template>
            <p>
              Your message has been sent. We'll get back to you as soon as
              possible.
            </p>
          </Alert>
          <template v-if="!finished">
            <Error :error="error"></Error>
            <form method="post" @submit.prevent="submit()">
              <FormGroup
                label="Your full name"
                small-label
                :error="v$.values.name.$error"
                required
                class="margin-bottom-2"
              >
                <FormInput
                  ref="name"
                  v-model="values.name"
                  :error="v$.values.name.$error"
                  size="large"
                  @blur="v$.values.name.$touch"
                ></FormInput>

                <template #error>
                  {{ v$.values.name.$errors[0]?.$message }}
                </template>
              </FormGroup>

              <FormGroup
                label="Your work email"
                small-label
                :error="v$.values.email.$error"
                required
                class="margin-bottom-2"
              >
                <FormInput
                  v-model="values.email"
                  size="large"
                  :error="v$.values.email.$error"
                  @blur="v$.values.email.$touch"
                ></FormInput>

                <template #error>
                  {{ v$.values.email.$errors[0]?.$message }}
                </template>
              </FormGroup>

              <FormGroup
                label="Your country"
                small-label
                :error="v$.values.country.$error"
                required
                class="margin-bottom-2"
              >
                <Dropdown
                  v-model="values.country"
                  :class="{
                    'dropdown--error': v$.values.country.$error,
                  }"
                  size="large"
                  @hide="v$.values.country.$touch"
                >
                  <DropdownItem
                    v-for="country in countries"
                    :key="country"
                    :name="country"
                    :value="country"
                  ></DropdownItem>
                </Dropdown>

                <template #error>
                  {{ v$.values.country.$errors[0]?.$message }}
                </template>
              </FormGroup>

              <FormGroup
                label="Your phone number"
                small-label
                class="margin-bottom-2"
              >
                <FormInput
                  ref="name"
                  v-model="values.phoneNumber"
                  size="large"
                ></FormInput>
              </FormGroup>

              <div class="row">
                <div class="col col-6">
                  <FormGroup
                    label="Company name"
                    small-label
                    :error="v$.values.companyName.$error"
                    required
                    class="margin-bottom-2"
                  >
                    <FormInput
                      ref="name"
                      v-model="values.companyName"
                      :error="v$.values.companyName.$error"
                      size="large"
                      @blur="v$.values.companyName.$touch"
                    ></FormInput>

                    <template #error>
                      {{ v$.values.companyName.$errors[0]?.$message }}
                    </template>
                  </FormGroup>
                </div>
                <div class="col col-6">
                  <FormGroup
                    label="Company size"
                    :error="v$.values.companySize.$error"
                    required
                    small-label
                    class="margin-bottom-2"
                  >
                    <Dropdown
                      v-model="values.companySize"
                      :class="{
                        'dropdown--error': v$.values.companySize.$error,
                      }"
                      size="large"
                      @hide="v$.values.companySize.$touch"
                    >
                      <DropdownItem
                        v-for="choice in companySizes"
                        :key="choice.value"
                        :name="choice.value"
                        :value="choice.value"
                      ></DropdownItem>
                    </Dropdown>

                    <template #error>
                      {{ v$.values.companySize.$errors[0]?.$message }}
                    </template>
                  </FormGroup>
                </div>
              </div>
              <div class="row">
                <div class="col col-6">
                  <FormGroup
                    label="Your team"
                    :error="v$.values.team.$error"
                    required
                    small-label
                    class="margin-bottom-2"
                  >
                    <Dropdown
                      v-model="values.team"
                      :class="{ 'dropdown--error': v$.values.team.$error }"
                      size="large"
                      @hide="v$.values.team.$touch"
                    >
                      <DropdownItem
                        v-for="choice in teamChoices"
                        :key="choice.value"
                        :name="choice.value"
                        :value="choice.value"
                      ></DropdownItem>
                    </Dropdown>

                    <template #error>
                      {{ v$.values.team.$errors[0]?.$message }}
                    </template>
                  </FormGroup>
                </div>
                <div class="col col-6">
                  <FormGroup
                    label="Your role"
                    :error="v$.values.role.$error"
                    class="margin-bottom-2"
                    small-label
                    required
                  >
                    <FormInput
                      ref="name"
                      v-model="values.role"
                      size="large"
                      :error="v$.values.role.$error"
                      @blur="v$.values.role.$touch"
                    ></FormInput>

                    <template #error>
                      {{ v$.values.role.$errors[0]?.$message }}
                    </template>
                  </FormGroup>
                </div>
              </div>
              <FormGroup
                label="Message"
                small-label
                :error="v$.values.message.$error"
                required
                class="margin-bottom-2"
              >
                <FormTextarea
                  v-model="values.message"
                  :error="v$.values.message.$error"
                  size="regular"
                  :rows="5"
                  :maxlength="1000"
                  @blur="v$.values.message.$touch"
                ></FormTextarea>

                <template #error>
                  {{ v$.values.message.$errors[0]?.$message }}
                </template>
              </FormGroup>

              <Alert
                v-if="containsGuestPostKeywords"
                type="warning"
                class="saas-contact__guest-post-alert margin-bottom-2"
              >
                <template #title>We don't accept guest posts</template>
                <p>
                  Your message looks like a guest post, sponsored article,
                  backlink or link placement request. We don't accept those
                  under any condition, and they're removed without a reply.
                  Please only use this form to contact our sales team about
                  using Baserow.
                </p>
                <FormGroup
                  :error="v$.values.guestPostAcknowledged.$error"
                  class="margin-top-2"
                >
                  <Checkbox
                    v-model="values.guestPostAcknowledged"
                    :error="v$.values.guestPostAcknowledged.$error"
                  >
                    I confirm that I'm not asking about a guest post, sponsored
                    content or link placement.
                  </Checkbox>

                  <template #error>
                    {{ v$.values.guestPostAcknowledged.$errors[0]?.$message }}
                  </template>
                </FormGroup>
              </Alert>

              <div class="actions">
                <Button
                  size="large"
                  :full-width="true"
                  :loading="loading"
                  :disabled="loading"
                >
                  Send message
                </Button>
              </div>
            </form>
          </template>
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { reactive } from 'vue'
import { required, email, maxLength, helpers } from '@vuelidate/validators'
import { ResponseErrorMessage } from '@baserow/modules/core/plugins/clientHandler'
import error from '@baserow/modules/core/mixins/error'
import { countryList } from '@baserow/modules/core/utils/countries'
import {
  getTeamChoices,
  getCompanySizeChoices,
  containsGuestPostKeywords,
} from '@saas/utils/sales'
import ContactService from '@saas/services/contact'

export default {
  mixins: [error],
  setup() {
    const values = reactive({
      values: {
        name: '',
        email: '',
        country: '',
        team: '',
        companySize: '',
        companyName: '',
        role: '',
        phoneNumber: '',
        message: '',
        guestPostAcknowledged: false,
      },
    })

    const rules = {
      values: {
        name: {
          required: helpers.withMessage('This field is required', required),
        },
        email: {
          required: helpers.withMessage('This field is required', required),
          email: helpers.withMessage(
            'Please enter a valid email address',
            email
          ),
        },
        country: {
          required: helpers.withMessage('This field is required', required),
        },
        team: {
          required: helpers.withMessage('This field is required', required),
        },
        companySize: {
          required: helpers.withMessage('This field is required', required),
        },
        companyName: {
          required: helpers.withMessage('This field is required', required),
          maxLength: helpers.withMessage(
            'This field has a maximum length of 100 characters',
            maxLength(100)
          ),
        },
        role: {
          required: helpers.withMessage('This field is required', required),
          maxLength: helpers.withMessage(
            'This field has a maximum length of 100 characters',
            maxLength(100)
          ),
        },
        message: {
          required: helpers.withMessage('This field is required', required),
          maxLength: helpers.withMessage(
            'This field has a maximum length of 1000 characters',
            maxLength(1000)
          ),
        },
        guestPostAcknowledged: {
          acknowledged: helpers.withMessage(
            'You must confirm this before you can send your message',
            (value) =>
              value === true ||
              !containsGuestPostKeywords(
                values.values.message,
                values.values.companyName,
                values.values.role
              )
          ),
        },
      },
    }

    return {
      values: values.values,
      v$: useVuelidate(rules, values, {
        $lazy: true,
        $scope: 'contact-sales-form',
      }),
    }
  },
  data() {
    return {
      loading: false,
      finished: false,
    }
  },
  mounted() {
    useHead({
      title: 'Contact sales',
    })
  },
  computed: {
    countries() {
      return countryList
    },
    teamChoices() {
      return getTeamChoices(this)
    },
    companySizes() {
      return getCompanySizeChoices(this)
    },
    containsGuestPostKeywords() {
      return containsGuestPostKeywords(
        this.values.message,
        this.values.companyName,
        this.values.role
      )
    },
  },
  methods: {
    async submit() {
      this.v$.$touch()
      if (this.v$.$invalid) {
        return
      }

      this.loading = true
      this.hideError()

      try {
        await ContactService(this.$client).salesSubmit(
          this.values,
          JSON.parse(localStorage.getItem('trackedParams') || '{}')
        )
        this.loading = false
        this.finished = true
        this.$nextTick(() => {
          this.$refs.form.scrollIntoView({ behavior: 'smooth' })
        })
      } catch (error) {
        this.loading = false
        this.handleError(error, 'contact', {
          ERROR_CONTACT_SUBMIT: new ResponseErrorMessage(
            'Something went wrong.',
            'Something went wrong while submitting the form. Our developers have been' +
              ' notified of this issue. Please try again in a few minutes.'
          ),
        })
      }
    },
  },
}
</script>
